import { createAsyncThunk } from "@reduxjs/toolkit";
import { API_ENDPOINTS } from "../../../constants/apiConstants";
import { CorporateEventStatusEnum } from "../../../constants/enums";
import {
	IAttachmentFile,
	ICorporateEvent,
	ICorporateEventBidsResponse,
	ICorporateEventWhatsAppGroupInfo,
	IDeclineCorporateEventForVendorPayload,
} from "../../../interfaces/corporateEvent";
import { iApiSuccess, iThunkApi } from "../../../types/commonTypes";
import { API_METHOD_TYPE, apiManager, isApiSuccess } from "../../apiManager";
import {
	transformCorporateEventsListingResponse,
	transformCorporateEventDetailResponse,
} from "../../../utils/transformers/coporateEventTransformers";
import { REACT_APP_CMS_URL } from "../../../constants/envConstants";
import { apiCommonManager } from "../../apiCommonManager";
import { EVENT_TYPE_HISTORY } from "../../../types/eventTypes";
import { ICreateCorporateQuoteForm } from "../../../types/quoteFormTypes";

export const getCorporateEventsThunk = createAsyncThunk<
	ICorporateEvent[],
	CorporateEventStatusEnum,
	iThunkApi
>("corporate/corporate-events", async (status, thunkAPI) => {
	const resp = await apiManager(
		API_ENDPOINTS.GET_CORPORATE_EVENTS_LIST(status),
		null,
		API_METHOD_TYPE.GET,
		{},
	);
	if (!resp || !isApiSuccess(resp.status)) {
		return thunkAPI.rejectWithValue(resp.data);
	}
	return transformCorporateEventsListingResponse(resp.data.data?.corporateEventsVendors || []);
});

export const getCorporateEventsHistoryThunk = createAsyncThunk<
	ICorporateEvent[],
	EVENT_TYPE_HISTORY,
	iThunkApi
>("corporate/corporate-events-history", async (type, thunkAPI) => {
	const resp = await apiManager(API_ENDPOINTS.CORPORATE_EVENT_HISTORY(type), null, API_METHOD_TYPE.GET, {});
	if (!resp || !isApiSuccess(resp.status)) {
		return thunkAPI.rejectWithValue(resp.data);
	}
	return transformCorporateEventsListingResponse(resp.data.data?.corporateEventsVendors || []);
});

export const getCorporateEventDetailThunk = createAsyncThunk<ICorporateEvent, string, iThunkApi>(
	"corporate/get-corporate-event-detail",
	async (id, thunkAPI) => {
		const resp = await apiManager(
			API_ENDPOINTS.GET_CORPORATE_EVENT_DETAIL(id),
			null,
			API_METHOD_TYPE.GET,
			{},
		);
		if (!resp || !isApiSuccess(resp.status)) {
			return thunkAPI.rejectWithValue(resp.data);
		}
		return transformCorporateEventDetailResponse(resp.data?.data?.corporateEventVendor);
	},
);

export const getCorporateEventWhatsAppGroupRecord = createAsyncThunk<
	ICorporateEventWhatsAppGroupInfo,
	{ corporateEventVendorId: string },
	iThunkApi
>("corporate/get-corporate-event-whatsapp-group-record", async ({ corporateEventVendorId }, thunk) => {
	const response = await apiManager(
		API_ENDPOINTS.GET_CORPORATE_EVENT_WHATS_APP_GROUP_INFO(corporateEventVendorId),
		{},
		API_METHOD_TYPE.GET,
		{},
	);

	if (response && response.data && isApiSuccess(response.status)) {
		return response.data.data?.groupInfo;
	}

	return thunk.rejectWithValue(response?.data);
});

export const getCorporateEventWhatsAppGroupLink = createAsyncThunk<
	{ data: { invitationLink: string } },
	{ corporateEventVendorId: string },
	iThunkApi
>("corporate/get-corporate-event-whatsapp-group-link", async ({ corporateEventVendorId }, thunk) => {
	const response = await apiManager(
		API_ENDPOINTS.GET_CORPORATE_EVENT_WHATS_APP_LINK(corporateEventVendorId),
		{},
		API_METHOD_TYPE.GET,
		{},
	);

	if (response && response.data && isApiSuccess(response.status)) {
		return response.data;
	}

	return thunk.rejectWithValue(response?.data);
});

export const getCorporateEventBidsThunk = createAsyncThunk<ICorporateEventBidsResponse, string, iThunkApi>(
	"corporate/get-corporate-event-bids",
	async (corporateEventId, thunkAPI) => {
		const resp = await apiManager(
			API_ENDPOINTS.GET_CORPORATE_EVENT_BIDS(corporateEventId),
			null,
			API_METHOD_TYPE.GET,
			{},
		);
		if (!resp || !isApiSuccess(resp.status)) {
			return thunkAPI.rejectWithValue(resp.data);
		}
		return resp.data?.data;
	},
);

export const declineCorporateEventThunk = createAsyncThunk<
	iApiSuccess,
	IDeclineCorporateEventForVendorPayload,
	iThunkApi
>("corporate/decline-corporate-event-for-vendor", async (payload, thunkAPI) => {
	const resp = await apiManager(
		API_ENDPOINTS.DECLINE_CORPORATE_EVENT(payload.corporateEventId),
		payload.body,
		API_METHOD_TYPE.POST,
		{},
	);
	if (!resp || !isApiSuccess(resp.status)) {
		return thunkAPI.rejectWithValue(resp.data);
	}
	return resp.data?.data;
});

export const uploadMultipleFiles = createAsyncThunk<
	IAttachmentFile[],
	{ files: File[]; directory: string },
	iThunkApi
>("upload-multiple-files", async (payload, thunkAPI) => {
	const formData = new FormData();
	payload.files.forEach((file) => {
		formData.append("files", file);
	});
	formData.append("directory", payload.directory);
	const resp = await apiCommonManager.request(
		API_ENDPOINTS.UPLOAD_MULTIPLE_FILES,
		formData,
		API_METHOD_TYPE.POST,
		"",
		REACT_APP_CMS_URL,
		true,
	);
	if (!resp || !isApiSuccess(resp.status)) {
		return thunkAPI.rejectWithValue(resp.data);
	}
	return resp.data.data;
});

export const submitCorporateEventBidThunk = createAsyncThunk<
	iApiSuccess,
	ICreateCorporateQuoteForm,
	iThunkApi
>("corporate/submit-corporate-event-bid", async (payload, thunkAPI) => {
	const resp = await apiManager(API_ENDPOINTS.SAVE_CORPORATE_EVENT_BID, payload, API_METHOD_TYPE.POST, {});
	if (!resp || !isApiSuccess(resp.status)) {
		return thunkAPI.rejectWithValue(resp.data);
	}
	return resp.data;
});
